import React, { useEffect, useRef, useState } from "react";

/**
 * Resmo Mascot — fluffy orange beast in a sharp navy suit jacket.
 * Hidden entirely in ProMo via global CSS (`[data-mode="promo"]`).
 *
 * State drives a CSS animation, not different artwork:
 *   idle       — gentle bounce
 *   hungry     — anticipation shake
 *   chewing    — actively eating (chew animation + words flowing above head + thought bubble)
 *   digesting  — pulse glow + thought bubble + drifting words
 *   satisfied  — a beaming halo
 *
 * Artwork is also swapped via the `flavor` prop:
 *   default — uses the standard Resmo portrait everywhere; the "thinking"
 *             pose appears for chewing/digesting (pondering chin)
 *   resume  — used on resume-tailoring flows (FeedJob, BulkResult). Cycles
 *             between the eating + chewing portraits while processing, and
 *             ends on the triumphant "delivering tailored resume" portrait
 *             when satisfied.
 *
 * Optional props:
 *   bubbleText — override the rotating thought-bubble text
 *   words      — array of word chips that fly across above the head
 */
const ANIM = {
  idle: "anim-bounce",
  hungry: "anim-shake",
  chewing: "anim-chew",
  digesting: "anim-pulse",
  satisfied: "",
};

const DEFAULT_WORDS = [
  "skills", "impact", "metrics", "leadership", "results",
  "stack", "experience", "growth", "ownership", "shipped",
  "team", "ATS-ready",
];

const DEFAULT_BUBBLES = [
  "This resume is tasty — I think something good is coming...",
  "Mmm, juicy keywords. Chewing carefully...",
  "Digesting your experience — almost there...",
  "Resmo is hungry for impact bullets — feed me more!",
  "Tasting the tone — corporate, friendly, or bold?",
  "Crunching the numbers in your achievements...",
];

const EATING_STATES = new Set(["chewing", "digesting"]);

// Image library — keep in sync with /app/frontend/public/brand/
const IMG = {
  default: "/brand/resmo-character.png",
  welcoming: "/brand/resmo-welcoming.png",
  thinking: "/brand/resmo-thinking.png",
  eating: "/brand/resmo-eating.png",
  chewing: "/brand/resmo-chewing.png",
  delivering: "/brand/resmo-delivering.png",
};

// Pick the right portrait for the current (state, flavor) pair.
function pickImageSrc({ state, flavor }) {
  // Universal loading pose — the welcoming Resmo (full body, both arms) is
  // used while any background work is in flight. Consistent visual language
  // across every page: Studio, RoleResearch, Compare, BulkResult, Dashboard.
  if (state === "chewing" || state === "digesting") return IMG.welcoming;

  // Resume tailoring flow gets the triumphant two-armed delivering Resmo
  // when results land. Every other flow falls back to the default portrait.
  if (state === "satisfied" && flavor === "resume") return IMG.delivering;

  return IMG.default;
}

export default function ResmoMascot({
  state = "idle",
  size = 220,
  flavor = "default",
  bubbleText,
  words,
}) {
  const isSatisfied = state === "satisfied";
  const isEating = EATING_STATES.has(state) && size >= 140;
  const animClass = ANIM[state] || "";

  // Detect "results just landed" — fires once when state transitions from
  // chewing/digesting -> satisfied while in resume flavor. Used to play a
  // one-second sparkle burst + "Bon appétit" reveal bubble.
  const [revealing, setRevealing] = useState(false);
  const prevStateRef = useRef(state);
  useEffect(() => {
    const prev = prevStateRef.current;
    if (
      flavor === "resume" &&
      state === "satisfied" &&
      EATING_STATES.has(prev) &&
      size >= 140
    ) {
      setRevealing(true);
      const id = setTimeout(() => setRevealing(false), 1800);
      prevStateRef.current = state;
      return () => clearTimeout(id);
    }
    prevStateRef.current = state;
    return undefined;
  }, [state, flavor, size]);

  // Cycle bubble messages every ~4.5s while Resmo is eating.
  const [bubbleIdx, setBubbleIdx] = useState(0);
  useEffect(() => {
    if (!isEating || bubbleText) return undefined;
    const id = setInterval(() => {
      setBubbleIdx((i) => (i + 1) % DEFAULT_BUBBLES.length);
    }, 4500);
    return () => clearInterval(id);
  }, [isEating, bubbleText]);

  // Removed the eating/chewing cycle — keeping a single calm portrait
  // during processing prevents the close-up images from looking distorted.

  const activeBubble = bubbleText || DEFAULT_BUBBLES[bubbleIdx];
  const flyingWords = (words && words.length ? words : DEFAULT_WORDS).slice(0, 8);
  const imgSrc = pickImageSrc({ state, flavor });

  return (
    <div
      className="relative inline-block"
      style={{ width: size, height: size + (isEating ? 100 : 0) }}
      data-testid="monster-mascot"
    >
      {/* Thought bubble — only while actively processing */}
      {isEating && (
        <div
          className="absolute left-1/2 anim-thought-float"
          style={{
            top: -8,
            transform: "translateX(-50%)",
            zIndex: 4,
            pointerEvents: "none",
          }}
          data-testid="mascot-thought-bubble"
        >
          {/* Small cloud-puff trail */}
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              bottom: -10,
              width: 16,
              height: 16,
              background: "white",
              borderRadius: "50%",
              border: "2px solid #0F1B2D",
              boxShadow: "0 -8px 0 -2px white, 0 -8px 0 0 #0F1B2D",
            }}
          />
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              bottom: -22,
              left: "calc(50% - 12px)",
              width: 8,
              height: 8,
              background: "white",
              borderRadius: "50%",
              border: "2px solid #0F1B2D",
            }}
          />
          {/* Main bubble */}
          <div
            className="font-mono-rm text-[11px] leading-snug px-3 py-2 max-w-[240px] text-center"
            style={{
              background: "white",
              border: "2px solid #0F1B2D",
              borderRadius: 14,
              boxShadow: "3px 3px 0 0 #FFC145",
              color: "#0F1B2D",
              fontWeight: 600,
              minWidth: 160,
            }}
          >
            {activeBubble}
          </div>
        </div>
      )}

      {/* Flying word chips — slide right-to-left in a band ABOVE Resmo's head. */}
      {isEating && (
        <div
          className="absolute"
          style={{
            top: 60,
            left: -size * 0.15,
            right: -size * 0.15,
            height: 28,
            pointerEvents: "none",
            zIndex: 2,
            overflow: "hidden",
          }}
          data-testid="mascot-flying-words"
        >
          {flyingWords.map((word, i) => {
            const totalDuration = 6.4;
            const delay = ((i * totalDuration) / flyingWords.length).toFixed(2);
            const yJiggle = (i % 3) * 4;
            return (
              <span
                key={word + i}
                className="absolute font-mono-rm text-[10px] font-bold whitespace-nowrap"
                style={{
                  right: -80,
                  top: yJiggle,
                  padding: "3px 8px",
                  background: "#FFC145",
                  color: "#0F1B2D",
                  border: "2px solid #0F1B2D",
                  borderRadius: 999,
                  boxShadow: "2px 2px 0 0 #0F1B2D",
                  animation: `word-fly-across ${totalDuration}s linear ${delay}s infinite`,
                  opacity: 0,
                  letterSpacing: "0.02em",
                }}
                data-testid={`mascot-word-${i}`}
              >
                {word}
              </span>
            );
          })}
        </div>
      )}

      {/* Halo glow when satisfied */}
      {isSatisfied && (
        <div
          className="absolute"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            background:
              "radial-gradient(circle, rgba(255, 193, 69, 0.55) 0%, transparent 65%)",
            filter: "blur(8px)",
            zIndex: 0,
            animation: "pulse-glow 2.4s ease-in-out infinite",
          }}
        />
      )}

      {/* Ground shadow — keeps Resmo grounded now that backgrounds are
          transparent. Pulses in sync with the bounce so the shadow tightens
          as he lifts. Suppressed for the chewing/digesting states (the
          chew animation has no vertical lift to play against). */}
      {state !== "chewing" && state !== "digesting" && (
        <div
          className="absolute pointer-events-none"
          style={{
            // Sit just under the mascot's feet — accounts for the image's
            // 100px offset when "isEating" is true (defensive even though
            // we suppress this branch on chewing/digesting).
            left: "50%",
            top: (isEating ? 100 : 0) + size - 10,
            width: size * 0.62,
            height: size * 0.10,
            transform: "translateX(-50%)",
            background:
              "radial-gradient(ellipse, rgba(15,27,45,0.35) 0%, rgba(15,27,45,0.18) 50%, transparent 75%)",
            borderRadius: "50%",
            zIndex: 0,
            animation: state === "idle"
              ? "ground-shadow-pulse 4s ease-in-out infinite"
              : "none",
            opacity: state === "satisfied" ? 0.45 : 0.32,
          }}
          data-testid="mascot-ground-shadow"
        />
      )}

      <img
        src={imgSrc}
        alt="Resmo — your career intelligence companion"
        className={animClass}
        style={{
          width: size,
          height: size,
          objectFit: "contain",
          position: "absolute",
          left: 0,
          top: isEating ? 100 : 0,
          zIndex: 1,
          transformOrigin: "center bottom",
          filter: isSatisfied ? "drop-shadow(0 0 16px rgba(255, 193, 69, 0.5))" : "none",
          transition: "opacity 220ms ease",
        }}
        draggable={false}
      />

      {/* Reveal moment — fires once when results land on resume-tailoring
          flows. Renders a sparkle ring + a celebratory thought bubble. */}
      {revealing && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: 0, right: 0, top: 0, bottom: 0,
            zIndex: 5,
          }}
          data-testid="mascot-reveal-burst"
        >
          {/* "Bon appétit" celebratory bubble */}
          <div
            className="absolute left-1/2"
            style={{
              top: -4,
              transform: "translateX(-50%)",
              animation: "reveal-pop 1.4s ease-out forwards",
            }}
            data-testid="mascot-reveal-bubble"
          >
            <div
              className="absolute left-1/2 -translate-x-1/2"
              style={{
                bottom: -10,
                width: 16,
                height: 16,
                background: "white",
                borderRadius: "50%",
                border: "2px solid #047857",
              }}
            />
            <div
              className="font-mono-rm text-[11px] leading-snug px-3 py-2 max-w-[260px] text-center"
              style={{
                background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
                border: "2px solid #047857",
                borderRadius: 14,
                boxShadow: "3px 3px 0 0 #FFC145",
                color: "#064E3B",
                fontWeight: 700,
                minWidth: 200,
              }}
            >
              ✨ Mmm — your tailored resume is ready. Bon appétit!
            </div>
          </div>

          {/* Sparkle ring — 10 little stars burst outward from the mascot. */}
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i / 12) * Math.PI * 2;
            const dist = size * 0.45;
            const dx = Math.cos(angle) * dist;
            const dy = Math.sin(angle) * dist;
            const delay = (i * 0.04).toFixed(2);
            return (
              <span
                key={i}
                className="absolute"
                style={{
                  left: "50%",
                  top: `${size * 0.5 + (isEating ? 100 : 0)}px`,
                  width: 10,
                  height: 10,
                  background: i % 2 === 0 ? "#FFC145" : "#10B981",
                  borderRadius: "50%",
                  boxShadow: "0 0 8px rgba(255, 193, 69, 0.8)",
                  opacity: 0,
                  ["--rx"]: `${dx}px`,
                  ["--ry"]: `${dy}px`,
                  animation: `sparkle-burst 1.2s ease-out ${delay}s forwards`,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
